import { blinkConstants } from '@thoughtspot/blink-constants';
import { formatDate } from '@thoughtspot/date-format-util';
import { getMinDifferentiatingPrecision, mapArrayToBooleanHash, } from '@thoughtspot/js-util';
import { DEFAULT_DECIMAL_PRECISION, getDefaultCurrencyCode, getValueFormattedByConfig, safeNumberFormat, } from '@thoughtspot/number-format-util';
import { enableInferCurrencyFromBrowserAtViz } from '@thoughtspot/session-service';
import { getEmptyValuePlaceHolder, getNullValuePlaceHolder, } from '@thoughtspot/special-data-value-format';
import { assign } from '@thoughtspot/string-util';
import { strings } from '@thoughtspot/translations';
import _ from 'lodash';
import { CategoryType, CurrencyFormatType, NegativeValueFormat, Unit, } from '../types/prism';
import { truncate } from '../utils/string';
import { chartSpecificConstants, chartTypesNeedOrdinalXAxis, chartTypesNotUsingMeasureAsOrdinalOnXAxis, COMPOSITE_COLUMN_VALUES_JOIN_KEY, getLabelWithHtml, useMeasureOnXAxisAsOrdinal, } from './chart-type-formatter-util';
import { compareNumberFormats } from './format-config-util';
import { getFormatterOfTypeEnum, INFINITY_HTML, INFINITY_SYMBOL, } from './formatter-util';
import { AXIS_TYPES, } from './types';
export const getLocaleName = (col) => {
    let locale = null;
    if (col.isCurrency &&
        col.currencyTypeInfo.settingType !== CurrencyFormatType.Column) {
        if (col.currencyTypeInfo.settingType === CurrencyFormatType.IsoCode) {
            locale = col.currencyTypeInfo.isoCode;
        }
        else if (col.currencyCode) {
            locale = col.currencyCode;
        }
        else {
            locale = getDefaultCurrencyCode();
        }
    }
    return locale;
};
export function formatInfinityValue(value, tickInterval, showInfinity, showMinusInfinity, minValue, maxValue, infinityPlaceholderValue, axisType) {
    if (!(showInfinity || showMinusInfinity)) {
        return null;
    }
    if (showInfinity) {
        if (value >= infinityPlaceholderValue) {
            return assign(INFINITY_HTML, '', _.get(INFINITY_SYMBOL, `${axisType}_AXIS_POSITIVE`));
        }
        if (value > maxValue + tickInterval) {
            return '';
        }
    }
    if (showMinusInfinity) {
        if (value <= infinityPlaceholderValue) {
            return assign(INFINITY_HTML, '', _.get(INFINITY_SYMBOL, `${axisType}_AXIS_NEGATIVE`));
        }
        if (value < minValue - tickInterval) {
            return '';
        }
    }
    return undefined;
}
function getInfinityValue(column, value) {
    if (!column.isEffectivelyNumeric || !column.columnHasInfinityValues) {
        return undefined;
    }
    if (value >= column.columnInfinityPlaceholder) {
        return '∞';
    }
    if (value <= column.columnNegativeInfinityPlaceholder) {
        return '-∞';
    }
    return undefined;
}
export const extractCaptionTitleAndLink = (valueObjectOrArray) => {
    const value = valueObjectOrArray &&
        _.get(_.castArray(valueObjectOrArray), '0.value', valueObjectOrArray);
    const captionTitleAndLink = _.isString(value) && value.match(blinkConstants.URL_CAPTION_REGEX);
    if (!captionTitleAndLink) {
        return undefined;
    }
    return {
        caption: captionTitleAndLink[1],
        link: value.replace(captionTitleAndLink[0], ''),
    };
};
export const getFormattedNumericColumnJsonValue = (value, column, allColumnValuesOnAxis) => {
    const isDouble = column.isDouble;
    let nDecimal;
    let noShorten = false;
    if (isDouble) {
        if (allColumnValuesOnAxis && allColumnValuesOnAxis.length > 1) {
            nDecimal = getMinDifferentiatingPrecision(allColumnValuesOnAxis, blinkConstants.decimalPrecision.MAX);
            if (column.isEffectivelyPercent) {
                nDecimal = Math.max(DEFAULT_DECIMAL_PRECISION, nDecimal - 2);
            }
        }
        else {
            nDecimal = blinkConstants.decimalPrecision.MIN;
        }
        noShorten = nDecimal > 0;
    }
    return getFormatterOfTypeEnum(column.formatterTypeEnum)(value, {
        noShorten,
        nDecimal,
        ...column,
    });
};
export function getLabelForNumericAxisOrColumn(value, format, allColumnValuesOnAxis, column) {
    const formatPattern = column.formatPattern;
    const columnLocale = getLocaleName(column);
    if (format) {
        if (column.isGrowth ||
            (!(format.category === CategoryType.Custom) &&
                column.columnToFormatPatternType === 'CURRENCY') ||
            column.columnToFormatPatternType === 'NONE' ||
            _.isEmpty(column.columnToFormatPatternType)) {
            return getValueFormattedByConfig(value, format, {
                formatPattern,
                columnLocale,
            });
        }
    }
    return getFormattedNumericColumnJsonValue(value, column, allColumnValuesOnAxis);
}
export function getFullLabelForYAxis(value, axis, tickPositions, tickInterval, yAxisColumn, minValue, maxValue, showInfinity, showMinusInfinity, infinityPlaceholderValue, captionAndLink) {
    const valueLabelToConsider = _.isObject(captionAndLink)
        ? captionAndLink.caption
        : value;
    const infinityLabel = formatInfinityValue(valueLabelToConsider, tickInterval, showInfinity, showMinusInfinity, minValue, maxValue, infinityPlaceholderValue, AXIS_TYPES.Y);
    if (infinityLabel) {
        return infinityLabel;
    }
    const yAxisLabel = getLabelForNumericAxisOrColumn(valueLabelToConsider, axis.formatting, tickPositions, yAxisColumn);
    if (yAxisLabel) {
        return yAxisLabel;
    }
    if (_.isObject(captionAndLink)) {
        return captionAndLink.caption;
    }
    return valueLabelToConsider;
}
function isPrimarySortOnXAxisColumns(xAxisColumns, yAxisColumns) {
    let maxXAxisColSortIndex = Number.NEGATIVE_INFINITY;
    for (let i = 0; i < xAxisColumns.length; i++) {
        const xAxisColumn = xAxisColumns[i];
        if (!xAxisColumn.isSorted) {
            return false;
        }
        maxXAxisColSortIndex = Math.max(maxXAxisColSortIndex, xAxisColumn.sortIndex);
    }
    return !yAxisColumns.some(yAxisColumn => yAxisColumn.isSorted &&
        yAxisColumn.sortIndex < maxXAxisColSortIndex);
}
function columnsMakeTimeSeries(xAxisColumns) {
    return (xAxisColumns.length === 1 &&
        xAxisColumns[0].isDateColumn &&
        xAxisColumns[0].isCalendarGregorianOrFiscal);
}
function isXAxisOrdinalBased(chartType, xAxisColumns, yAxisColumns) {
    const chartTypesNeedOrdinalXAxisMap = mapArrayToBooleanHash(chartTypesNeedOrdinalXAxis);
    if (chartTypesNeedOrdinalXAxisMap[chartType]) {
        return true;
    }
    const chartTypesNotUsingMeasureAsOrdinalMap = mapArrayToBooleanHash(chartTypesNotUsingMeasureAsOrdinalOnXAxis);
    const useMeasureAsOrdinal = !chartTypesNotUsingMeasureAsOrdinalMap[chartType];
    const isXAxisMeasure = xAxisColumns.length === 1 && xAxisColumns[0].isEffectivelyNumeric;
    return !((isXAxisMeasure && !useMeasureAsOrdinal) ||
        (columnsMakeTimeSeries(xAxisColumns) &&
            isPrimarySortOnXAxisColumns(xAxisColumns, yAxisColumns)));
}
export const formatCompositeAxisValue = function (values, allColumns, axis) {
    return values
        .map(valueObject => {
        const columnEffectiveId = valueObject.columnEffectiveId;
        const column = allColumns[columnEffectiveId];
        const formatPattern = column.formatPattern;
        const columnLocale = getLocaleName(column);
        if (!column) {
            return valueObject.value;
        }
        if (column.isColumnValidForNumberFormatting) {
            return getValueFormattedByConfig(valueObject.value, axis.formatting, { formatPattern, columnLocale });
        }
        return getFormatterOfTypeEnum(column.formatterTypeEnum)(valueObject.value, {
            noShorten: false,
        }, column);
    })
        .join(COMPOSITE_COLUMN_VALUES_JOIN_KEY);
};
const formatCompositeColumnValue = (values, allColumns) => values
    .map(valueObject => {
    const columnEffectiveId = valueObject.columnEffectiveId;
    const column = allColumns[columnEffectiveId];
    const formatPattern = column.formatPattern;
    const columnLocale = getLocaleName(column);
    if (!column) {
        return valueObject.value;
    }
    const formatConfig = column.format;
    if (formatConfig && column.isColumnValidForNumberFormatting) {
        return getValueFormattedByConfig(valueObject.value, formatConfig, { formatPattern, columnLocale });
    }
    return getFormatterOfTypeEnum(column.formatterTypeEnum)(valueObject.value, {
        noShorten: false,
    }, column);
})
    .join(COMPOSITE_COLUMN_VALUES_JOIN_KEY);
export function getXAxisLabelAt(x, allColumns, xAxisColumnIds, yAxisColumnIds, axis, chartType, xValueToRawValues, allXValuesOnLabels, options) {
    const firstXAxisColumn = allColumns[xAxisColumnIds[0]];
    const useMeasureAsOrdinal = useMeasureOnXAxisAsOrdinal(chartType);
    let xValue;
    let xValues;
    if (xAxisColumnIds.length === 1 &&
        firstXAxisColumn.isColumnValidForNumberFormatting) {
        if (!useMeasureAsOrdinal) {
            xValue = x;
            xValues = allXValuesOnLabels;
        }
        else {
            if (!xValueToRawValues[x]) {
                return '';
            }
            xValue = xValueToRawValues[x][0].value;
            xValues =
                allXValuesOnLabels &&
                    allXValuesOnLabels.map(index => {
                        if (!xValueToRawValues[index]) {
                            return null;
                        }
                        return xValueToRawValues[index][0].value;
                    }).compact();
        }
        return getLabelForNumericAxisOrColumn(xValue, axis.formatting, xValues, firstXAxisColumn);
    }
    const xAxisColumnsConfig = xAxisColumnIds.map(colId => allColumns[colId]);
    const yAxisColumnsConfig = yAxisColumnIds.map(colId => allColumns[colId]);
    const columnMakesTimeSeriesAndYIsNotPrimarySorted = columnsMakeTimeSeries(xAxisColumnsConfig) &&
        isPrimarySortOnXAxisColumns(xAxisColumnsConfig, yAxisColumnsConfig);
    if (!_.isInteger(x) && !columnMakesTimeSeriesAndYIsNotPrimarySorted) {
        return '';
    }
    if (columnMakesTimeSeriesAndYIsNotPrimarySorted) {
        let timestampValue;
        const xAxisIsOrdinalBased = isXAxisOrdinalBased(chartType, xAxisColumnsConfig, yAxisColumnsConfig);
        if (!xAxisIsOrdinalBased) {
            timestampValue = x;
        }
        else {
            if (!xValueToRawValues[x]) {
                return '';
            }
            timestampValue = xValueToRawValues[x][0].value;
        }
        return getFormatterOfTypeEnum(firstXAxisColumn.formatterTypeEnum)(timestampValue, options, firstXAxisColumn);
    }
    xValues = xValueToRawValues[x];
    if (_.isUndefined(xValues)) {
        return '';
    }
    return formatCompositeAxisValue(xValues, allColumns, axis);
}
export function getYAxisLabelForAttribute(column, value, rawValue) {
    if (column.isDateColumn || column.isTimeColumn || column.isDateNumColumn) {
        if (column.isYAxisOrdinalBased) {
            value = rawValue;
            if (!value || value.value === null || value.value === undefined) {
                return strings.NULL_VALUE_PLACEHOLDER_LABEL;
            }
            value = value.value;
        }
        return getFormatterOfTypeEnum(column.formatterTypeEnum)(value, {}, column);
    }
    const yValue = rawValue;
    if (!yValue) {
        return undefined;
    }
    if (_.isNil(yValue.value) || _.isNaN(yValue.value))
        return getNullValuePlaceHolder();
    if (!_.isNumber(yValue.value) &&
        !_.isBoolean(yValue.value) &&
        _.isEmpty(yValue.value))
        return getEmptyValuePlaceHolder();
    return yValue.value;
}
export const getNumericColumnJsonFormatPattern = (col) => {
    const defaultBusinessFormatDecimalCount = 2;
    const decimalCount = col.isFloat ? 2 : 0;
    const formatConfig = {};
    const userDefinedFormatConfig = col.format;
    if (col.isGrowth) {
        const percentFormatConfig = {
            decimals: userDefinedFormatConfig?.percentageFormatConfig?.decimals ??
                defaultBusinessFormatDecimalCount,
        };
        formatConfig.category = CategoryType.Percentage;
        formatConfig.percentageFormatConfig = percentFormatConfig;
    }
    else if (col.formatPattern || col.isColumnToFormatPatternType) {
        const customFormatConfig = {};
        customFormatConfig.format = col.formatPattern;
        formatConfig.category = CategoryType.Custom;
        formatConfig.customFormatConfig = customFormatConfig;
    }
    else if (col.isPercent) {
        const percentFormatConfig = {
            decimals: defaultBusinessFormatDecimalCount,
        };
        formatConfig.category = CategoryType.Percentage;
        formatConfig.percentageFormatConfig = percentFormatConfig;
    }
    else if (col.isCurrency) {
        const currencyFormatConfig = {};
        currencyFormatConfig.toSeparateThousands = true;
        currencyFormatConfig.unit = Unit.Auto;
        if (col.currencyTypeInfo.settingType === CurrencyFormatType.IsoCode) {
            currencyFormatConfig.locale = col.currencyTypeInfo
                .isoCode;
        }
        else if (col.currencyCode) {
            currencyFormatConfig.locale = col.currencyCode;
        }
        else {
            currencyFormatConfig.locale = enableInferCurrencyFromBrowserAtViz()
                ? blinkConstants.USER_LOCALE
                : getDefaultCurrencyCode();
        }
        currencyFormatConfig.decimals = decimalCount;
        formatConfig.category = CategoryType.Currency;
        formatConfig.currencyFormatConfig = currencyFormatConfig;
    }
    else {
        const numberFormatConfig = {};
        numberFormatConfig.negativeValueFormat = NegativeValueFormat.PrefixDash;
        numberFormatConfig.toSeparateThousands = true;
        numberFormatConfig.unit = Unit.Auto;
        formatConfig.category = CategoryType.Number;
        formatConfig.numberFormatConfig = numberFormatConfig;
        numberFormatConfig.decimals = decimalCount;
    }
    return formatConfig;
};
export const getFormattedValueForNumericColumnJson = (value, column) => {
    const formatPattern = column.formatPattern;
    const columnLocale = getLocaleName(column);
    const userDefinedFormatConfig = column.format;
    if (column.isGrowth) {
        const formatConfig = getNumericColumnJsonFormatPattern(column);
        return getValueFormattedByConfig(value, formatConfig, {
            formatPattern,
            columnLocale,
        });
    }
    if (userDefinedFormatConfig &&
        userDefinedFormatConfig.customFormatConfig &&
        _.isEmpty(userDefinedFormatConfig.customFormatConfig.format)) {
        return getFormatterOfTypeEnum(column.formatterTypeEnum)(value, {}, column);
    }
    if (typeof value === 'number') {
        if (userDefinedFormatConfig) {
            return getValueFormattedByConfig(value, userDefinedFormatConfig, {
                formatPattern,
                columnLocale,
            });
        }
        const formatConfig = getNumericColumnJsonFormatPattern(column);
        return getValueFormattedByConfig(value, formatConfig, {
            formatPattern,
            columnLocale,
        });
    }
    return getFormatterOfTypeEnum(column.formatterTypeEnum)(value, {}, column);
};
export function percentPointFormatter(percentage, x, y, column, props, maxCharacters) {
    const axis = _.find(props.axes, axisObj => !!axisObj.linkedColumns.find(colId => colId === column.id));
    const name = truncate(getXAxisColumnLabelAt(x, props.xAxisColumns, props.yAxisColumns, props.chartType, props.xValueToRawValues[x], props.allColumns), chartSpecificConstants.MAX_LABEL_LENGTH_PIE);
    const valueDataLabel = formatPercentDataLabel(percentage, y, column, maxCharacters);
    if (column.showDataLabels) {
        return `${name} - ${valueDataLabel}`;
    }
    return name;
}
export function formatPercentDataLabel(percentage, y, column, maxCharacters) {
    let value = getFormattedValueForNumericColumnJson(y, column);
    const percent = safeNumberFormat({
        style: 'decimal',
        maximumFractionDigits: 2,
    }, percentage);
    if (_.isNumber(maxCharacters)) {
        value = truncate(value, maxCharacters);
    }
    return `${value} (${percent}%)`;
}
export function getXAxisColumnLabelAt(x, xAxisColumnIds, yAxisColumnIds, chartType, xValues, allColumns) {
    const firstXAxisColumn = allColumns[xAxisColumnIds[0]];
    const xAxisColumnConfigs = xAxisColumnIds.map(colId => allColumns[colId]);
    const yAxisColumnConfigs = yAxisColumnIds.map(colId => allColumns[colId]);
    const useMeasureAsOrdinal = useMeasureOnXAxisAsOrdinal(chartType);
    if (xAxisColumnIds.length === 1 &&
        firstXAxisColumn.isColumnValidForNumberFormatting) {
        const value = !useMeasureAsOrdinal ? x : xValues[0].value;
        return getFormattedValueForNumericColumnJson(value, firstXAxisColumn);
    }
    const isTimeSeries = columnsMakeTimeSeries(xAxisColumnConfigs) &&
        isPrimarySortOnXAxisColumns(xAxisColumnConfigs, yAxisColumnConfigs);
    if (!_.isInteger(x) && !isTimeSeries) {
        return '';
    }
    if (isTimeSeries) {
        let timestampValue;
        const xAxisIsOrdinalBased = isXAxisOrdinalBased(chartType, xAxisColumnConfigs, yAxisColumnConfigs);
        if (!xAxisIsOrdinalBased) {
            timestampValue = x;
        }
        else {
            if (!xValues) {
                return '';
            }
            timestampValue = xValues[0].value;
        }
        return getFormatterOfTypeEnum(firstXAxisColumn.formatterTypeEnum)(timestampValue, {}, firstXAxisColumn);
    }
    if (_.isUndefined(xValues)) {
        return '';
    }
    return formatCompositeColumnValue(xValues, allColumns);
}
export function getStackLabelFormatting(allColumns, yAxisColumns, axis) {
    const allYAxisColumns = yAxisColumns.map(colId => _.get(allColumns, [colId]));
    const columns = allYAxisColumns.filter(col => axis.linkedColumns.find(colId => colId === col.id));
    let stackedTotalLabelFormat = _.assign({}, columns[0].format);
    columns.forEach((col) => {
        const columnFormat = col.format;
        if (_.isNil(columnFormat.currencyFormatConfig) &&
            _.isNil(columnFormat.numberFormatConfig)) {
            return;
        }
        if (!_.isNil(columnFormat.currencyFormatConfig)) {
            if (_.isNil(stackedTotalLabelFormat.currencyFormatConfig)) {
                stackedTotalLabelFormat = _.assign({}, columnFormat);
            }
            else if (compareNumberFormats(stackedTotalLabelFormat.currencyFormatConfig, columnFormat.currencyFormatConfig) > 0) {
                stackedTotalLabelFormat.currencyFormatConfig =
                    columnFormat.currencyFormatConfig;
            }
        }
        if (!_.isNil(columnFormat.numberFormatConfig) &&
            !_.isNil(stackedTotalLabelFormat.numberFormatConfig) &&
            compareNumberFormats(stackedTotalLabelFormat.numberFormatConfig, columnFormat.numberFormatConfig) > 0) {
            stackedTotalLabelFormat.numberFormatConfig =
                columnFormat.numberFormatConfig;
        }
    });
    return stackedTotalLabelFormat;
}
export function formatYAxisLabel(value, axis, tickPositions, tickInterval, yAxisColumn, limit, showInfinity, showMinusInfinity, infinityPlaceholderValue, yAxisToRawValues, maxCharacters, isYAxisOrdinalBased) {
    const dataRowIndex = yAxisColumn.dataRowIndex;
    let valueAtY;
    if (!!yAxisToRawValues && !!yAxisToRawValues[dataRowIndex]) {
        valueAtY = _.get(yAxisToRawValues, `${dataRowIndex}.${value}`);
        if (valueAtY && isYAxisOrdinalBased) {
            value = valueAtY.value;
        }
    }
    let captionAndLink;
    if (valueAtY) {
        captionAndLink = extractCaptionTitleAndLink(valueAtY);
    }
    let fullLabel = getFullLabelForYAxis(value, axis, tickPositions, tickInterval, yAxisColumn, limit.axisMin, limit.axisMax, showInfinity, showMinusInfinity, infinityPlaceholderValue, captionAndLink);
    if (!_.isNil(maxCharacters)) {
        fullLabel = truncate(_.toString(fullLabel), maxCharacters);
    }
    return getLabelWithHtml(fullLabel, !!captionAndLink && captionAndLink.link);
}
export function formatXAxisLabel(value, axisId, tickPositions, tickInterval, limit, showInfinity, showMinusInfinity, infinityPlaceholderValue, captionAndLink, params) {
    let format;
    let minYear;
    let maxYear;
    let omitYear;
    if (params.isTimeSeries && !params.isXAxisOrdinalBased) {
        format = 'yyyy';
        minYear = formatDate(limit.dataMin, format);
        maxYear = formatDate(limit.dataMax, format);
        omitYear = minYear === maxYear;
    }
    const valueLabelToConsider = captionAndLink
        ? captionAndLink.caption
        : value;
    const infinityLabel = formatInfinityValue(valueLabelToConsider, tickInterval, showInfinity, showMinusInfinity, minYear || limit.axisMin, maxYear || limit.axisMax, infinityPlaceholderValue, AXIS_TYPES.X);
    if (infinityLabel) {
        return infinityLabel;
    }
    const xAxisLabel = getXAxisLabelAt(valueLabelToConsider, params.allColumns, params.xAxisColumns, params.yAxisColumns, params.axes[axisId], params.chartType, params.xValueToRawValues, tickPositions, { omitYear });
    if (xAxisLabel) {
        if (!_.isNil(params.maxCharacters)) {
            return truncate(xAxisLabel, params.maxCharacters);
        }
        return xAxisLabel;
    }
    return getLabelWithHtml(valueLabelToConsider, !!captionAndLink && captionAndLink.link);
}
export const getDataLabelFromColumn = (labelColumn, value, yValueToRawValues) => {
    if (!labelColumn.isColumnValidForNumberFormatting) {
        const rawValue = yValueToRawValues[labelColumn.dataRowIndex][value];
        return getYAxisLabelForAttribute(labelColumn, value, rawValue);
    }
    const infinityValue = getInfinityValue(labelColumn, value);
    if (infinityValue) {
        return infinityValue;
    }
    return getFormattedValueForNumericColumnJson(value, labelColumn);
};
export const getFormattedValueForColumnTooltip = (column, value) => {
    const format = column.format;
    if (format && column.isColumnValidForNumberFormatting) {
        return getValueFormattedByConfig(value, format, {
            formatPattern: column.formatPattern,
            columnLocale: getLocaleName(column),
        });
    }
    return getFormatterOfTypeEnum(column.formatterTypeEnum)(value, {}, column);
};
//# sourceMappingURL=formatter-service.js.map